import {
  createContext,
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useLoading } from "@/components/LoadingContext";

export const DataLoadingContext = createContext();

export function DataLoadingProvider({ children, user }) {
  const { showLoading, hideLoading } = useLoading();

  // State for all data
  const [isInitializing, setIsInitializing] = useState(true);
  const [userDoc, setUserDoc] = useState(null);
  const [companyUsers, setCompanyUsers] = useState(null);
  const [selectedCompanyUser, setSelectedCompanyUser] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [affiliations, setAffiliations] = useState(null);

  // Track if we've shown loading once
  const hasShownLoading = useRef(false);

  // Load all data in parallel on mount
  useEffect(() => {
    if (!user?.uid) return;

    const unsubscribers = [];
    let isActive = true;

    // Show loading only once on initial mount
    if (!hasShownLoading.current) {
      hasShownLoading.current = true;
      showLoading();
    }

    // 1. Load user document
    const userUnsub = onSnapshot(
      doc(db, "users", user.uid),
      (docSnapshot) => {
        if (isActive && docSnapshot.exists()) {
          setUserDoc({ ...docSnapshot.data(), id: docSnapshot.id });
        }
      },
      (error) => {
        console.error("Error loading user document:", error);
      }
    );
    unsubscribers.push(userUnsub);

    // 2. Load company users
    const companyUsersUnsub = onSnapshot(
      query(collection(db, "companyUsers"), where("userId", "==", user.uid)),
      async (snapshot) => {
        if (!isActive) return;

        const results = [];
        snapshot.forEach((doc) => {
          if (doc.data().status !== "deleted") {
            results.push({ ...doc.data(), id: doc.id });
          }
        });

        // Create NOCOMPANY doc if needed
        if (results.length === 0) {
          const docId = `${user.uid}_NOCOMPANY`;
          const docRef = doc(db, "companyUsers", docId);

          try {
            const existing = await getDoc(docRef);

            if (!existing.exists()) {
              const userDocData = userDoc || {};
              await setDoc(docRef, {
                companyId: "NOCOMPANY",
                companyName: "Selecione...",
                createdAt: new Date().getTime(),
                permissions: ["owner"],
                role: "owner",
                status: "active",
                userId: user.uid,
                userName: userDocData.name || user.displayName || "",
                email: userDocData.email || user.email || "",
                id: docId,
                api_path: "DataLoadingProvider",
              });
              // Add the created doc to results
              results.push({
                companyId: "NOCOMPANY",
                companyName: "Selecione...",
                permissions: ["owner"],
                role: "owner",
                status: "active",
                userId: user.uid,
                userName: userDocData.name || user.displayName || "",
                email: userDocData.email || user.email || "",
                id: docId,
                api_path: "DataLoadingProvider",
              });
            }
          } catch (error) {
            console.error("Error creating NOCOMPANY doc:", error);
          }
        }

        setCompanyUsers(results);
      },
      (error) => {
        console.error("Error loading company users:", error);
        setCompanyUsers([]);
      }
    );
    unsubscribers.push(companyUsersUnsub);

    // Cleanup
    return () => {
      isActive = false;
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [user?.uid, user?.email, showLoading]);

  // Handle selected company user
  useEffect(() => {
    if (!companyUsers || companyUsers.length === 0 || !userDoc) return;

    // Avoid setting if already set to the same value
    const currentId = selectedCompanyUser?.companyId;

    if (userDoc.lastSelectedCompany && companyUsers.length) {
      // Skip if lastSelectedCompany is NOCOMPANY and we have real companies
      if (
        userDoc.lastSelectedCompany === "NOCOMPANY" &&
        companyUsers.some((c) => c.companyId !== "NOCOMPANY") &&
        currentId !== "NOCOMPANY"
      ) {
        return; // Keep current selection
      }

      const found = companyUsers.find(
        (cu) => cu.companyId === userDoc.lastSelectedCompany
      );

      if (found && found.companyId !== currentId) {
        setSelectedCompanyUser(found);
      } else if (!found && companyUsers[0].companyId !== currentId) {
        setSelectedCompanyUser(companyUsers[0]);
      }
    } else if (companyUsers.length && !currentId) {
      setSelectedCompanyUser(companyUsers[0]);
    }
  }, [companyUsers, userDoc, selectedCompanyUser?.companyId]);

  // Load company-specific data when selectedCompanyUser changes
  useEffect(() => {
    if (!selectedCompanyUser || !user) return;

    const companyId = selectedCompanyUser.companyId;
    const unsubscribers = [];

    // Skip loading for NOCOMPANY
    if (companyId === "NOCOMPANY") {
      setRecipient(null);
      setAffiliations([]);
      return;
    }

    // Load recipient using Firestore listener
    const recipientUnsub = onSnapshot(
      query(collection(db, "recipients"), where("companyId", "==", companyId)),
      (snapshot) => {
        if (!snapshot.empty) {
          const recipientDoc = snapshot.docs[0];
          setRecipient({ ...recipientDoc.data(), id: recipientDoc.id });
        } else {
          setRecipient(null);
        }
      },
      (error) => {
        console.error("Error loading recipient:", error);
        setRecipient(null);
      }
    );
    unsubscribers.push(recipientUnsub);

    // Load affiliations using Firestore listener
    const affiliationsUnsub =
      selectedCompanyUser.status === "active"
        ? onSnapshot(
            query(
              collection(db, "affiliations"),
              where("affiliateCompanyId", "==", companyId)
            ),
            (snapshot) => {
              const results = [];
              snapshot.forEach((doc) => {
                results.push({ ...doc.data(), id: doc.id });
              });
              setAffiliations(results);
            },
            (error) => {
              console.error("Error loading affiliations:", error);
              setAffiliations([]);
            }
          )
        : null;

    if (affiliationsUnsub) {
      unsubscribers.push(affiliationsUnsub);
    } else {
      setAffiliations([]);
    }

    // Cleanup
    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [selectedCompanyUser?.companyId, user]);

  // Determine when initial loading is complete
  useEffect(() => {
    const isReady =
      userDoc !== null &&
      companyUsers !== null &&
      selectedCompanyUser !== null &&
      (selectedCompanyUser?.companyId === "NOCOMPANY" || affiliations !== null);

    if (isReady && isInitializing) {
      setIsInitializing(false);
      hideLoading();
    }
  }, [
    userDoc,
    companyUsers,
    selectedCompanyUser,
    affiliations,
    isInitializing,
    hideLoading,
  ]);

  // Memoized setSelectedCompanyUser to prevent unnecessary re-renders
  const handleSetSelectedCompanyUser = useCallback((newCompanyUser) => {
    setSelectedCompanyUser(newCompanyUser);
  }, []);

  // Don't render children until we have at least the basic data
  if (!userDoc || !companyUsers || !selectedCompanyUser) {
    return null;
  }

  const isNoCompany = selectedCompanyUser?.companyId === "NOCOMPANY";

  return (
    <DataLoadingContext.Provider
      value={{
        userDoc,
        companyUsers,
        selectedCompanyUser,
        setSelectedCompanyUser: handleSetSelectedCompanyUser,
        recipient,
        affiliations,
        affiliationsMini:
          affiliations?.map((a) => ({
            id: a.id,
            affiliateCode: a.id.split("_")[0],
            productId: a.productId,
            status: a.status,
          })) || [],
        isNoCompany,
        isInitializing,
      }}
    >
      {children}
    </DataLoadingContext.Provider>
  );
}

// Hook to use the data loading context
export const useDataLoading = () => {
  const context = useContext(DataLoadingContext);
  if (!context) {
    throw new Error("useDataLoading must be used within DataLoadingProvider");
  }
  return context;
};
